import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DiagnosisReportContent from '../../components/diagnosis/DiagnosisReportContent';
import { calculateFinancialDiagnosis, calculateBusinessDiagnosis } from '../../utils/diagnosisEngine';
import { generateProfessionalPDF } from '../../utils/pdfGenerator';
import { getRoadmapSettings, getRoadmapByReportId } from '../../services/roadmap.service';
import { getDiagnosisReportById } from '../../services/diagnosisReports.service';
import ModernReport from '../../components/report/ModernReport';
import './ReportLandingPage.css';

const ReportLandingPage = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const [reportData, setReportData] = useState(null);
    const [calculations, setCalculations] = useState(null);
    const [viewMode, setViewMode] = useState('landing'); // 'landing' or 'report'
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [roadmapModuleEnabled, setRoadmapModuleEnabled] = useState(true);
    const [reportScale, setReportScale] = useState(1);
    const [roadmapData, setRoadmapData] = useState(null);

    // Dynamic scaling for mobile based on screen width
    useEffect(() => {
        const updateScale = () => {
            if (window.innerWidth < 850) {
                // We use 810 to provide some breathing room around the 794px width
                const newScale = window.innerWidth / 810;
                setReportScale(Math.min(newScale, 1));
            } else {
                setReportScale(1);
            }
        };
        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, []);

    const handleBuildRoadmap = () => {
        navigate(`/roadmap-form/${reportId}`);
    };

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            try {
                const firebaseData = await getDiagnosisReportById(reportId);
                if (firebaseData) {
                    if (firebaseData.status === 'incomplete' && !firebaseData.payment?.paymentId) {
                        console.warn("Payment not completed, redirecting.");
                        navigate('/');
                        return;
                    }
                    setReportData(firebaseData);
                    performCalculations(firebaseData);
                    localStorage.setItem('diagnosisReport', JSON.stringify(firebaseData));
                    
                    // Also fetch roadmap data if available
                    try {
                        const roadmap = await getRoadmapByReportId(reportId);
                        if (roadmap) {
                            setRoadmapData(roadmap);
                        }
                    } catch (rdmErr) {
                        console.warn("Could not fetch roadmap for PDF:", rdmErr);
                    }
                    setLoading(false);
                    return;
                } else {
                    navigate('/');
                }
            } catch (error) {
                console.error("Error fetching from Firebase, falling back to localStorage:", error);
                const storedData = localStorage.getItem('diagnosisReport');
                if (storedData) {
                    try {
                        const data = JSON.parse(storedData);
                        if (data.reportId === reportId || data.id === reportId) {
                            if (data.status === 'incomplete' && !data.payment?.paymentId) {
                                navigate('/');
                                return;
                            }
                            setReportData(data);
                            performCalculations(data);
                            setLoading(false);
                            return;
                        }
                    } catch (e) { console.error(e); }
                }
                navigate('/');
            } finally {
                setLoading(false);
            }
        };
        fetchReportData();

        // Fetch roadmap settings status
        const fetchSettings = async () => {
            try {
                const settings = await getRoadmapSettings();
                setRoadmapModuleEnabled(settings?.enabled !== false); // Default to true unless explicitly false
            } catch (err) {
                console.error("SETTINGS_FETCH_ERROR:", err);
            }
        };
        fetchSettings();
    }, [reportId, navigate]);

    const performCalculations = (data) => {
        try {
            const incomeType = data.personalInfo?.incomeType || 'salary';
            if (incomeType === 'salary') {
                const dr = calculateFinancialDiagnosis(data);
                setCalculations({
                    ...dr,
                    incomeType: 'salary',
                    healthScore: dr?.grades?.overall || 65,
                    savingsPercentage: (dr?.metrics?.savingsRate || 0).toFixed(1)
                });
            } else {
                const biz = calculateBusinessDiagnosis(data);
                setCalculations({
                    ...biz,
                    incomeType: 'business',
                    healthScore: biz?.grades?.overall || 65,
                    savingsPercentage: (biz?.metrics?.reserveRatio || 0).toFixed(1)
                });
            }
        } catch (err) {
            console.error("LANDING_PAGE_CALC_ERROR:", err);
            // FAIL SAFE: Set defaults so component loads anyway
            setCalculations({ metrics: {}, grades: { overall: 65 }, diagnosis: {}, recommendations: [], incomeType: 'salary', healthScore: 65, savingsPercentage: 0 });
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);

    const handleDownload = async () => {
        try {
            setDownloading(true);

            // Detect mobile device
            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

            try {
                // Always re-run the engine at download time to pick up any calculation fixes
                const { calculateFinancialDiagnosis: freshCalc, calculateBusinessDiagnosis: freshBizCalc } = await import('../../utils/diagnosisEngine');
                const incomeType = reportData?.personalInfo?.incomeType || 'salary';
                let freshCalculations;
                try {
                    // Combine reportData and roadmapData for the engine
                    const mergedForEngine = { ...reportData, ...(roadmapData || {}) };
                    freshCalculations = incomeType === 'salary'
                        ? { ...freshCalc(mergedForEngine), incomeType: 'salary' }
                        : { ...freshBizCalc(mergedForEngine), incomeType: 'business' };
                } catch (calcErr) {
                    console.warn("Fresh calc failed, using cached:", calcErr);
                    freshCalculations = calculations;
                }

                // Generate PDF - this will handle mobile-specific display/download
                await generateProfessionalPDF({ ...reportData, ...(roadmapData || {}) }, freshCalculations);

                // Note: PDF generator now handles mobile alerts internally
                // Just reset the downloading state after a delay
                setTimeout(() => {
                    setDownloading(false);
                }, isMobile ? 2000 : 500);

            } catch (err) {
                console.error("PDF_ASYNC_ERROR:", err);
                setDownloading(false);
                if (isMobile) {
                    alert("PDF generation failed. Please use the 'View Report' button and take screenshots, or try on a desktop computer.");
                } else {
                    alert("Error generating PDF. Please try again.");
                }
            }
        } catch (err) {
            console.error("PDF_GEN_ERROR:", err);
            setDownloading(false);
        }
    };

    if (loading || !reportData || !calculations) return <div className="loading">Checking payment status...</div>;

    return (
        <div className="report-page-root">
            {viewMode === 'landing' ? (
                <div className="report-landing-wrapper">
                    <div className="report-landing-container">
                        <div className="success-header">
                            <div className="success-icon">✓</div>
                            <h1>Payment Successful!</h1>
                            <p>Your Financial Diagnosis Report is Ready</p>
                        </div>

                        <div className="summary-cards">
                            <div className="s-card"><span>Financial Health</span><strong>{calculations.healthScore}/100</strong></div>
                            <div className="s-card">
                                <span>{calculations.incomeType === 'business' ? 'Reserve Ratio' : 'Savings Rate'}</span>
                                <strong>{calculations.savingsPercentage}%</strong>
                            </div>
                        </div>

                        <div className="report-actions-container">
                            <div className="report-actions">
                                <button onClick={() => setViewMode('report')} className="btn-secondary">View Report</button>
                                <button
                                    onClick={handleDownload}
                                    className="btn-primary"
                                    disabled={downloading}
                                >
                                    {downloading ? (
                                        <span>
                                            <span className="spinner-mini"></span>
                                            Generating PDF (29 pages)...
                                        </span>
                                    ) : (
                                        'Download PDF'
                                    )}
                                </button>
                                {roadmapModuleEnabled && (
                                    <button onClick={handleBuildRoadmap} className="btn-roadmap">Build Roadmap</button>
                                )}
                            </div>
                            {downloading && (
                                <div className="download-hint">
                                    📱 Preparing your complete 29-page report...<br />
                                    <small>🔐 PDF will be password protected</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="full-report-viewer">
                    <div className="viewer-controls no-print">
                        <div className="viewer-logo">
                            <img src="/Yathartha-Logo.png" alt="Yathartha Logo" style={{ height: '32px', width: 'auto' }} />
                        </div>
                        <div className="viewer-btns">
                            <button onClick={() => window.print()} className="control-btn-light">Print / Save</button>
                            <button onClick={() => setViewMode('landing')} className="control-btn-dark">Close</button>
                        </div>
                    </div>

                    <div className="modern-report-scroll-wrapper">
                        <div className="report-scale-container" style={{
                            height: viewMode === 'report' ? `calc(5700px * ${reportScale})` : 'auto'
                        }}>
                            <div className="modern-container-shadow" style={{
                                transform: `scale(${reportScale})`,
                                transformOrigin: 'top center'
                            }}>
                                <ModernReport data={reportData} calculations={calculations} isSummary={true} />
                            </div>
                        </div>
                    </div>

                    <style>{`
                        @media print { .no-print { display: none !important; } }
                                               .full-report-viewer {
                            position: fixed;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            width: 100%;
                            height: 100%;
                            background: #f0f2f5 !important;
                            z-index: 99999;
                            overflow-y: auto;
                            overflow-x: hidden;
                            display: flex;
                            flex-direction: column;
                            -webkit-overflow-scrolling: touch;
                        }

                        .viewer-controls {
                            position: sticky; 
                            top: 0; 
                            background: #1a7a5e; 
                            padding: 10px 15px; 
                            display: flex; 
                            justify-content: space-between; 
                            align-items: center; 
                            z-index: 10000; 
                            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                        }
                        .viewer-logo { color: white; display: flex; align-items: center; }
                        .viewer-btns { display: flex; gap: 8px; }

                        .control-btn-light { background: white; color: #1a7a5e; border: none; padding: 7px 12px; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 12px; }
                        .control-btn-dark { background: rgba(0,0,0,0.15); color: white; border: 1px solid rgba(255,255,255,0.3); padding: 7px 12px; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 12px; }
                        
                        .modern-report-scroll-wrapper { 
                            background: #f0f2f5; 
                            width: 100%;
                            display: flex;
                            justify-content: center;
                            overflow-x: hidden;
                            padding-bottom: 50px;
                        }
                        
                        .report-scale-container {
                            width: 100%;
                            display: flex;
                            justify-content: center;
                            overflow: hidden;
                        }

                        .modern-container-shadow {
                            box-shadow: 0 0 50px rgba(0,0,0,0.1);
                            width: 794px; 
                            background: white;
                            flex-shrink: 0;
                        }

                        @media (max-width: 850px) {
                            .viewer-controls { padding: 8px 10px; }
                            .control-btn-light, .control-btn-dark { padding: 6px 8px; font-size: 11px; }
                            .report-scale-container { padding: 0; }
                        }
                    `}</style>
                </div>
            )}

            <style>{`
                .report-page-root { font-family: 'Inter', sans-serif; background: #f9fafb; min-height: 100vh; }
                .report-landing-container { max-width: 800px; margin: 60px auto; text-align: center; padding: 50px; background: white; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
                .success-icon { background: #d1fae5; color: #059669; width: 70px; height: 70px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 35px; margin: 0 auto 25px; }
                .summary-cards { display: flex; justify-content: center; gap: 20px; margin: 40px 0; }
                .s-card { background: #f8faf9; padding: 20px; border-radius: 12px; min-width: 170px; border: 1px solid #e8f5f0; flex: 1; }
                .s-card strong { display: block; font-size: 22px; color: #1a7a5e; margin-top: 5px; }
                .report-actions { display: flex; justify-content: center; align-items: center; gap: 20px; margin-top: 30px; flex-wrap: wrap; }
                .security-text { font-size: 12px; color: #64748b; margin-top: 20px; text-align: center; line-height: 1.5; padding: 0 20px; }
                .btn-primary { background: #1a7a5e; color: white; padding: 15px 40px; border-radius: 35px; border: none; cursor: pointer; font-weight: 700; font-size: 16px; transition: all 0.2s; min-width: 220px; }
                .btn-primary:hover { background: #155f49; transform: translateY(-2px); }
                .btn-secondary { background: white; color: #1a7a5e; border: 2px solid #1a7a5e; padding: 15px 40px; border-radius: 35px; cursor: pointer; font-weight: 700; font-size: 16px; transition: all 0.2s; min-width: 220px; }
                .btn-secondary:hover { background: #f8faf9; transform: translateY(-2px); }
                .btn-roadmap { background: #f59e0b; color: white; border: none; padding: 15px 40px; border-radius: 35px; cursor: pointer; font-weight: 700; font-size: 16px; transition: all 0.2s; min-width: 220px; }
                .btn-roadmap:hover { background: #d97706; transform: translateY(-2px); }
                .back-btn { background: #f3f4f6; border: none; padding: 10px 20px; border-radius: 8px; cursor: pointer; font-weight: 600; color: #4b5563; }

                @media (max-width: 768px) {
                    .report-landing-container { margin: 20px 15px; padding: 30px 20px; border-radius: 15px; }
                    .success-header h1 { font-size: 24px; }
                    .success-header p { font-size: 14px; }
                    .summary-cards { flex-direction: column; gap: 12px; margin: 30px 0; }
                    .s-card { min-width: unset; padding: 15px; }
                    .s-card strong { font-size: 18px; }
                    .report-actions { flex-direction: column; gap: 12px; }
                    .btn-primary, .btn-secondary, .btn-roadmap { width: 100%; min-width: unset; padding: 12px 20px; font-size: 15px; }
                }
            `}</style>
        </div>
    );
};

export default ReportLandingPage;
